/**
 * Strict view models for the live surfaces. Orval generates every response field as optional (the
 * springdoc schema does not mark them required), so we normalize the loose wire types ONCE here into
 * strict shapes with sensible defaults — the components + pure logic then read total types, never a
 * forest of `?? 0`. This mirrors the dashboard's defensive `isNum`/optional handling, centralized.
 */
import type {
  LiveFloorResponse,
  LiveFloorTableResponse,
  LiveFloorZoneResponse,
  LiveTimelineRowResponse,
} from "@repo/api-client";

export interface LiveRow {
  bookingId: string;
  externalBookingId: string;
  status: string;
  channel: string;
  serviceId: string;
  serviceDay: string;
  startSec: number;
  durationSec: number;
  partySize: number;
  guestGivenName: string | null;
  guestFamilyName: string | null;
  guestPhone: string | null;
  arrivedAtSec: number | null;
  seatedAtSec: number | null;
  lateAtSec: number;
  specialRequests: string | null;
  allergyNote: string | null;
  zonePreference: string | null;
  seatedTableIds: string[];
  contactFlagged: boolean;
  blacklistFlagged: boolean;
  pendingAssignment: boolean;
  version: number;
}

export interface LiveTable {
  tableId: string;
  zoneId: string;
  label: string;
  state: string;
  minCovers: number;
  maxCovers: number;
  backingBookingId: string | null;
  sinceSec: number | null;
  stuck: boolean;
}

export interface LiveZone {
  zoneId: string;
  name: string;
  standing: boolean;
  capacity: number | null;
  legalCapacity: number | null;
  occupiedCovers: number;
  nearLimit: boolean;
}

export interface LiveFloorData {
  generatedAtSec: number;
  zones: LiveZone[];
  tables: LiveTable[];
}

export function normRow(r: LiveTimelineRowResponse): LiveRow {
  return {
    bookingId: r.bookingId ?? "",
    externalBookingId: r.externalBookingId ?? "",
    status: r.status ?? "CONFIRMED",
    channel: r.channel ?? "PHONE",
    serviceId: r.serviceId ?? "",
    serviceDay: r.serviceDay ?? "",
    startSec: r.startSec ?? 0,
    durationSec: r.durationSec ?? 0,
    partySize: r.partySize ?? 0,
    guestGivenName: r.guestGivenName ?? null,
    guestFamilyName: r.guestFamilyName ?? null,
    guestPhone: r.guestPhone ?? null,
    arrivedAtSec: r.arrivedAtSec ?? null,
    seatedAtSec: r.seatedAtSec ?? null,
    lateAtSec: r.lateAtSec ?? 0,
    specialRequests: r.specialRequests ?? null,
    allergyNote: r.allergyNote ?? null,
    zonePreference: r.zonePreference ?? null,
    seatedTableIds: r.seatedTableIds ?? [],
    contactFlagged: r.contactFlagged ?? false,
    blacklistFlagged: r.blacklistFlagged ?? false,
    pendingAssignment: r.pendingAssignment ?? false,
    version: r.version ?? 0,
  };
}

export function normTable(t: LiveFloorTableResponse): LiveTable {
  return {
    tableId: t.tableId ?? "",
    zoneId: t.zoneId ?? "",
    label: t.label ?? "",
    state: t.state ?? "AVAILABLE",
    minCovers: t.minCovers ?? 0,
    maxCovers: t.maxCovers ?? 0,
    backingBookingId: t.backingBookingId ?? null,
    sinceSec: t.sinceSec ?? null,
    stuck: t.stuck ?? false,
  };
}

export function normZone(z: LiveFloorZoneResponse): LiveZone {
  return {
    zoneId: z.zoneId ?? "",
    name: z.name ?? "",
    standing: z.standing ?? false,
    capacity: z.capacity ?? null,
    legalCapacity: z.legalCapacity ?? null,
    occupiedCovers: z.occupiedCovers ?? 0,
    nearLimit: z.nearLimit ?? false,
  };
}

export function normFloor(f: LiveFloorResponse): LiveFloorData {
  return {
    generatedAtSec: f.generatedAtSec ?? 0,
    zones: (f.zones ?? []).map(normZone),
    tables: (f.tables ?? []).map(normTable),
  };
}
